import { locales } from '@/routing'
import { findOption } from '@/shared/utils/typesUtils'
import { useUpdateCompany } from '@/shared/hooks/useCompanies'
import { useCountries } from '@/shared/hooks/useCountry'
import { useDialogState } from '@/shared/hooks/useDialogState'
import type { Company } from '@/shared/models/companyModel'
import type { Identifier } from '@/shared/models/debtorModels'
import { inputMessageContext, useInputRequiredContext, inputStateContext } from '@/shared/utils/inputUtils'
import { DrawerClose, DrawerContent, DrawerRoot, DrawerTrigger } from '@/shared/v2/shared/components/Drawer/Drawer'
import Button from '@/shared/v2/shared/components/Button'
import LoadingButton from '@/shared/v2/shared/components/LoadingButton'
import { isEmpty, isNil } from 'lodash'
import { useLocale, useTranslations } from 'next-intl'
import { useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import Input from '@/shared/v2/shared/components/Input'
import Select from '@/shared/v2/shared/components/Select'
import PrivilegesButton from '@/shared/v2/shared/components/PrivilegesButton'

type UpdateProfileCompanyForm = {
    name: Company['name']
    identifier?: Identifier
}

const createDataTestId = (id: string) => `__update-settings-profile-company-${id}__`

const mapDefaultValues = (company: Company | undefined): UpdateProfileCompanyForm => ({
    name: company?.name ?? '',
    identifier: company?.identifier
        ? {
              type: company?.identifier.type,
              value: company?.identifier.value,
              country: company?.identifier.country
          }
        : undefined
})

export type UpdateSettingsProfileCompanyDrawerProps = {
    company: Company | undefined
}

const UpdateSettingsProfileCompanyDrawer = ({ company }: UpdateSettingsProfileCompanyDrawerProps) => {
    const settingsTranslations = useTranslations('settings')

    const updateCompany = useUpdateCompany()
    const defaultValues = useMemo(() => mapDefaultValues(company), [company])
    const { isOpen, onOpenChange } = useDialogState({
        shouldCloseIf: updateCompany.isSuccess
    })

    const locale = useLocale()

    const {
        formState: { isDirty, errors },
        control,
        watch,
        handleSubmit,
        register,
        reset
    } = useForm<UpdateProfileCompanyForm>({
        defaultValues
    })

    useEffect(() => {
        if (!isDirty) {
            reset(defaultValues)
        }
    }, [defaultValues, isDirty, reset])

    const getInputState = inputStateContext<UpdateProfileCompanyForm>(watch, errors)
    const getInputMessage = inputMessageContext<UpdateProfileCompanyForm>(errors)
    const getRequiredValue = useInputRequiredContext()

    const countries = useCountries(findOption(locales, locale))
    const country = watch('identifier.country')
    const identifierTypes = countries.data?.find((constraint) => constraint.country === country)?.identifiers

    const onSubmit = (payload: UpdateProfileCompanyForm) => {
        updateCompany.mutate(payload)
    }

    const mappedCountries = countries.data?.map((country) => ({
        value: country.country,
        label: country.label
    }))

    const resolveCountryValue = (value: Identifier['country'] | undefined | null) => {
        if (isNil(value)) return null

        const item = countries.data?.find((item) => item.country === value)

        return {
            value: item?.country,
            label: item?.label
        }
    }

    const resolveIdentifierTypeValue = (value: Identifier['type'] | undefined | null) => {
        if (isNil(value)) return null

        return {
            label: value.toUpperCase(),
            value
        }
    }
    const mappedIdentifierTypeOptions = identifierTypes?.map((type) => resolveIdentifierTypeValue(type))

    return (
        <DrawerRoot open={isOpen} onOpenChange={onOpenChange}>
            <DrawerTrigger data-testid={createDataTestId('trigger')} asChild>
                <PrivilegesButton requiredPrivilege="manager" intent="secondary">
                    {settingsTranslations('actions.edit')}
                </PrivilegesButton>
            </DrawerTrigger>
            <DrawerContent title={settingsTranslations('company.legalsInformationsTitle')}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="grid grid-cols-1 gap-y-5">
                        <Input
                            data-testid={createDataTestId('name')}
                            state={getInputState('name')}
                            message={getInputMessage('name')}
                            placeholder={settingsTranslations('company.update.inputs.name.placeholder')}
                            label={settingsTranslations('company.update.inputs.name.label')}
                            {...register('name', {
                                required: getRequiredValue()
                            })}
                        />
                        <Controller
                            control={control}
                            name="identifier.country"
                            rules={{ required: getRequiredValue() }}
                            render={({ field: { onChange, value } }) => {
                                return (
                                    <Select
                                        data-testid={createDataTestId('identifier-country')}
                                        placeholder={settingsTranslations(
                                            'company.update.inputs.identifierCountry.placeholder'
                                        )}
                                        label={settingsTranslations('company.update.inputs.identifierCountry.label')}
                                        required={true}
                                        state={getInputState('identifier.country')}
                                        message={getInputMessage('identifier.country')}
                                        options={mappedCountries}
                                        onChange={onChange}
                                        value={resolveCountryValue(value)}
                                    />
                                )
                            }}
                        />
                        <Controller
                            control={control}
                            name="identifier.type"
                            rules={{ required: getRequiredValue() }}
                            render={({ field: { onChange, value } }) => {
                                return (
                                    <Select<{ label: string; value: Identifier['type'] } | null>
                                        data-testid={createDataTestId('identifier-type')}
                                        isDisabled={isEmpty(watch('identifier.country'))}
                                        placeholder={settingsTranslations(
                                            'company.update.inputs.identifierType.placeholder'
                                        )}
                                        label={settingsTranslations('company.update.inputs.identifierType.label')}
                                        required={true}
                                        state={getInputState('identifier.type')}
                                        message={getInputMessage('identifier.type')}
                                        options={mappedIdentifierTypeOptions}
                                        onChange={onChange}
                                        value={resolveIdentifierTypeValue(value)}
                                    />
                                )
                            }}
                        />
                        <Input
                            data-testid={createDataTestId('identifier-value')}
                            state={getInputState('identifier.value')}
                            message={getInputMessage('identifier.value')}
                            placeholder={settingsTranslations('company.update.inputs.identifierValue.placeholder')}
                            label={settingsTranslations('company.update.inputs.identifierValue.label')}
                            {...register('identifier.value', {
                                required: getRequiredValue()
                            })}
                        />
                        <div className="grid grid-cols-2 gap-3 w-full">
                            <DrawerClose asChild>
                                <Button data-testid={createDataTestId('cancel')} intent="secondary">
                                    {settingsTranslations('actions.cancel')}
                                </Button>
                            </DrawerClose>
                            <LoadingButton
                                data-testid={createDataTestId('submit')}
                                isLoading={updateCompany.isPending}
                                type="submit"
                            >
                                {settingsTranslations('actions.save')}
                            </LoadingButton>
                        </div>
                    </div>
                </form>
            </DrawerContent>
        </DrawerRoot>
    )
}

export default UpdateSettingsProfileCompanyDrawer
